import React, { useState, useEffect, useCallback, useRef } from 'react';
import TiltCard from '../components/tw1/TiltCard';
import EmptyState from '../components/tw1/EmptyState';
import { CheckCircle2, Circle, Clock, ArrowRight, FileText, Users, ChevronLeft, ChevronRight, AlertCircle, Bell, Settings, Plus, Trash2, Save, X, RefreshCw, ExternalLink, Pin, PinOff, Cloud, Sun, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, CloudFog, Snowflake, Wind, Phone, ArrowRightLeft, Megaphone, Info, AlertTriangle, Flame, MessageCircle, Send, AtSign, GripVertical } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Textarea } from '../components/ui/textarea';
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { getUser, isAdmin } from '../lib/auth';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const NL_DAYS = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
const NL_MONTHS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
const monthNames = NL_MONTHS;

const DEFAULT_DAY_TYPES = [
  { value: 'fixed', label: 'Vaste dag' },
  { value: 'every_maandag', label: 'Elke maandag' },
  { value: 'every_dinsdag', label: 'Elke dinsdag' },
  { value: 'every_woensdag', label: 'Elke woensdag' },
  { value: 'every_donderdag', label: 'Elke donderdag' },
  { value: 'every_vrijdag', label: 'Elke vrijdag' },
  { value: 'last_thursday', label: 'Laatste donderdag' },
  { value: 'wed_before_last_thu', label: 'Wo vóór laatste do' },
  { value: 'last_day', label: 'Laatste dag' },
];
const DEFAULT_TASK_TYPES = ['Facturatie', 'Betalingen', 'Salaris'];
const EMOJI_OPTIONS = ['📄', '💸', '🏦', '💰', '📋', '📊', '✅', '🔔', '📦', '🚛'];

const AVATAR_COLORS = ['#003366', '#f5a800', '#1e8c45', '#e91e8c', '#c0392b', '#526071', '#ff6200', '#00244d'];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Goedemorgen';
  if (h < 18) return 'Goedemiddag';
  return 'Goedenavond';
}

function getFirstName(user) {
  if (user?.naam) return user.naam.split(' ')[0];
  if (user?.email) {
    const local = user.email.split('@')[0];
    return local.charAt(0).toUpperCase() + local.slice(1);
  }
  return 'Gebruiker';
}

function getFullDate() {
  const now = new Date();
  const day = NL_DAYS[now.getDay()];
  const dayNum = now.getDate();
  const month = NL_MONTHS[now.getMonth()];
  const year = now.getFullYear();
  return `${day.charAt(0).toUpperCase() + day.slice(1)} ${dayNum} ${month} ${year}`;
}

function getWmoIcon(code) {
  if (code <= 1) return Sun;
  if (code <= 3) return Cloud;
  if (code >= 51 && code <= 57) return CloudDrizzle;
  if (code >= 61 && code <= 67) return CloudRain;
  if (code >= 71 && code <= 77) return Snowflake;
  if (code >= 80 && code <= 82) return CloudRain;
  if (code >= 85 && code <= 86) return CloudSnow;
  if (code >= 95) return CloudLightning;
  if (code === 45 || code === 48) return CloudFog;
  return Cloud;
}

function getWmoDesc(code) {
  if (code === 0) return 'Helder';
  if (code <= 3) return 'Bewolkt';
  if (code === 45 || code === 48) return 'Mist';
  if (code >= 51 && code <= 57) return 'Motregen';
  if (code >= 61 && code <= 67) return 'Regen';
  if (code >= 71 && code <= 77) return 'Sneeuw';
  if (code >= 80 && code <= 82) return 'Buien';
  if (code >= 85 && code <= 86) return 'Sneeuwbuien';
  if (code >= 95) return 'Onweer';
  return 'Bewolkt';
}

// DnD helper components for task drag & drop
function DraggableTask({ id, children, disabled }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id, disabled });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 } : {};
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
}

function DroppableEmployee({ id, children, isOver }) {
  const { setNodeRef, isOver: hovering } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`transition-all duration-200 ${hovering ? 'ring-2 ring-[#f5a800] ring-offset-2 scale-[1.02]' : ''}`}>
      {children}
    </div>
  );
}

export default function DashboardPage({ onNavigate }) {
  const user = getUser();
  const admin = isAdmin();
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const day = now.getDate();
  const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;

  const [tasks, setTasks] = useState([]);
  const [recentExports, setRecentExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);

  // Widget states
  const [weather, setWeather] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [pinnedShortcuts, setPinnedShortcuts] = useState([]);
  const weatherCache = useRef({ data: null, timestamp: 0 });

  // Phone duty states
  const [phoneDutyToday, setPhoneDutyToday] = useState(null);
  const [phoneDutyWeek, setPhoneDutyWeek] = useState([]);
  const [phoneDutyEligible, setPhoneDutyEligible] = useState([]);
  const [swapping, setSwapping] = useState(null);

  // Announcements states
  const [announcements, setAnnouncements] = useState([]);
  const [showNewAnnouncement, setShowNewAnnouncement] = useState(false);
  const [annForm, setAnnForm] = useState({ message: '', type: 'info', expires_at: '' });

  // Task detail & comments states
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskComments, setTaskComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionCursorPos, setMentionCursorPos] = useState(0);
  const commentInputRef = useRef(null);

  // Drag & drop state
  const [activeDragId, setActiveDragId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Task management state
  const [showTaskMgmt, setShowTaskMgmt] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [editingTmpl, setEditingTmpl] = useState(null);
  const [tmplForm, setTmplForm] = useState({ label: '', icon: '📄', type: 'Facturatie', day_type: 'fixed', day: 1, medewerker: '' });
  const [newTypeName, setNewTypeName] = useState('');
  const [newDayTypeName, setNewDayTypeName] = useState('');
  const [newDayTypeKey, setNewDayTypeKey] = useState('');
  const [showAddType, setShowAddType] = useState(false);
  const [showAddDayType, setShowAddDayType] = useState(false);

  // Derive full lists: defaults + any custom values found in templates
  const allTaskTypes = [...new Set([...DEFAULT_TASK_TYPES, ...templates.map(t => t.type).filter(Boolean)])];
  const allDayTypes = [...DEFAULT_DAY_TYPES];
  templates.forEach(t => {
    if (t.day_type && !allDayTypes.find(d => d.value === t.day_type)) {
      allDayTypes.push({ value: t.day_type, label: t.day_type });
    }
  });

  const monthNames = ['januari','februari','maart','april','mei','juni','juli','augustus','september','oktober','november','december'];

  const fetchData = useCallback(async () => {
    try {
      const promises = [
        api.get(`/api/dashboard/tasks/${selectedYear}/${selectedMonth}`),
        api.get('/api/dashboard/recent-exports'),
        api.get('/api/sessions/active'),
        api.get('/api/activity?limit=8'),
        api.get('/api/users/basic'),
      ];
      const results = await Promise.all(promises);
      setTasks(results[0].data.tasks || []);
      setRecentExports(results[1].data || []);
      setSessions(results[2].data.sessions || []);
      setActivities(results[3].data.activities || []);
      const users = results[4].data.users || [];
      setAllUsers(users);
      // Load pinned shortcuts from current user
      const me = users.find(u => u.email === user?.email);
      setPinnedShortcuts(me?.pinned_shortcuts || []);
      // Fetch suggestions
      api.get('/api/dashboard/suggestions').then(r => setSuggestions(r.data.suggestions || [])).catch(() => {});
      // Fetch phone duty
      Promise.all([
        api.get('/api/phone-duty/today'),
        api.get('/api/phone-duty'),
        api.get('/api/phone-duty/eligible'),
      ]).then(([todayRes, weekRes, eligibleRes]) => {
        setPhoneDutyToday(todayRes.data.duty);
        setPhoneDutyWeek(weekRes.data.days || []);
        setPhoneDutyEligible(eligibleRes.data.users || []);
      }).catch(() => {});
      // Fetch announcements
      api.get('/api/announcements/active').then(r => setAnnouncements(r.data.announcements || [])).catch(() => {});
    } catch (err) {
      console.error('Dashboard laden mislukt:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, user?.email]);

  // Weather fetch with 15-min cache
  useEffect(() => {
    const now = Date.now();
    if (weatherCache.current.data && now - weatherCache.current.timestamp < 15 * 60 * 1000) {
      setWeather(weatherCache.current.data);
      return;
    }
    fetch('https://api.open-meteo.com/v1/forecast?latitude=52.08&longitude=4.75&current_weather=true')
      .then(r => r.json())
      .then(data => {
        if (data.current_weather) {
          weatherCache.current = { data: data.current_weather, timestamp: Date.now() };
          setWeather(data.current_weather);
        }
      })
      .catch(() => {});
  }, []);

  // Auto-refresh activities every 60s
  useEffect(() => {
    const iv = setInterval(() => {
      api.get('/api/activity?limit=8').then(r => setActivities(r.data.activities || [])).catch(() => {});
      api.get('/api/sessions/active').then(r => setSessions(r.data.sessions || [])).catch(() => {});
    }, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  const toggleTask = async (index) => {
    const updated = [...tasks];
    updated[index] = { ...updated[index], checked: !updated[index].checked };
    setTasks(updated);
    try {
      await api.put(`/api/dashboard/tasks/${selectedYear}/${selectedMonth}`, { tasks: updated });
    } catch (err) {
      console.error('Taak bijwerken mislukt:', err);
    }
  };

  const prevMonth = () => {
    if (selectedMonth === 1) { setSelectedYear(y => y - 1); setSelectedMonth(12); }
    else setSelectedMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (selectedMonth === 12) { setSelectedYear(y => y + 1); setSelectedMonth(1); }
    else setSelectedMonth(m => m + 1);
  };
  const goToCurrentMonth = () => {
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth() + 1);
  };

  // Task template management
  const loadTemplates = async () => {
    try {
      const res = await api.get('/api/dashboard/task-templates');
      setTemplates(res.data.templates || []);
    } catch (e) { /* silent */ }
  };

  const openTaskMgmt = () => { loadTemplates(); setShowTaskMgmt(true); setEditingTmpl(null); };

  const startAddTmpl = () => {
    setEditingTmpl('new');
    setTmplForm({ label: '', icon: '📄', type: 'Facturatie', day_type: 'fixed', day: 1, medewerker: '' });
  };

  const startEditTmpl = (t) => {
    setEditingTmpl(t.key);
    setTmplForm({ label: t.label, icon: t.icon || '📄', type: t.type || 'Facturatie', day_type: t.day_type, day: t.day || 1, medewerker: t.medewerker || '' });
  };

  const saveTmpl = async () => {
    try {
      if (editingTmpl === 'new') {
        const key = tmplForm.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 30) + '_' + Date.now();
        await api.post('/api/dashboard/task-templates', { ...tmplForm, key, day: tmplForm.day_type === 'fixed' ? tmplForm.day : null });
        toast.success('Taak toegevoegd');
      } else {
        await api.put(`/api/dashboard/task-templates/${editingTmpl}`, { ...tmplForm, day: tmplForm.day_type === 'fixed' ? tmplForm.day : null });
        toast.success('Taak bijgewerkt');
      }
      setEditingTmpl(null);
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Opslaan mislukt'); }
  };

  const deleteTmpl = async (key) => {
    try {
      await api.delete(`/api/dashboard/task-templates/${key}`);
      toast.success('Taak verwijderd');
      loadTemplates();
    } catch (e) { toast.error('Verwijderen mislukt'); }
  };

  const regenerateTasks = async () => {
    try {
      await api.post(`/api/dashboard/tasks/${selectedYear}/${selectedMonth}/regenerate`);
      toast.success('Taken opnieuw gegenereerd');
      fetchData();
      setShowTaskMgmt(false);
    } catch (e) { toast.error('Regenereren mislukt'); }
  };

  const checkedCount = tasks.filter(t => t.checked).length;
  const taskProgress = tasks.length > 0 ? Math.round((checkedCount / tasks.length) * 100) : 0;

  // Confetti: fire once when all tasks completed
  const confettiFired = React.useRef(false);
  useEffect(() => {
    if (tasks.length > 0 && checkedCount === tasks.length && !confettiFired.current) {
      confettiFired.current = true;
      const fire = (opts) => confetti({ ...opts, disableForReducedMotion: true });
      fire({ particleCount: 80, spread: 70, origin: { x: 0.3, y: 0.6 }, colors: ['#003366', '#f5a800', '#f5c518', '#00244d'] });
      setTimeout(() => fire({ particleCount: 60, spread: 90, origin: { x: 0.7, y: 0.5 }, colors: ['#003366', '#f5a800', '#1e8c45'] }), 300);
      setTimeout(() => fire({ particleCount: 50, spread: 100, origin: { x: 0.5, y: 0.4 }, colors: ['#f5a800', '#003366'] }), 600);
    }
    if (checkedCount < tasks.length) confettiFired.current = false;
  }, [checkedCount, tasks.length]);

  // Today's tasks and overdue
  const todayTasks = isCurrentMonth ? tasks.filter(t => t.dag === day && !t.checked) : [];
  const overdueTasks = isCurrentMonth ? tasks.filter(t => t.dag < day && !t.checked) : [];

  const dismissSuggestion = async (key) => {
    setSuggestions(s => s.filter(x => x.key !== key));
    api.post('/api/dashboard/suggestions/dismiss', { key }).catch(() => {});
  };

  // Sorted tasks: overdue first, then today, then future
  const sortedTasks = [...tasks].sort((a, b) => {
    const aOverdue = isCurrentMonth && a.dag < day && !a.checked;
    const bOverdue = isCurrentMonth && b.dag < day && !b.checked;
    const aToday = isCurrentMonth && a.dag === day;
    const bToday = isCurrentMonth && b.dag === day;
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;
    if (aToday && !bToday) return -1;
    if (!aToday && bToday) return 1;
    return a.dag - b.dag;
  });

  // Find original index for toggling
  const findOrigIndex = (task) => tasks.findIndex(t => t.key === task.key);

  // Shortcuts
  const EXTERNAL_LINKS = [
    { label: 'PTW3 Backend', url: 'https://bodegraven.online.protruckwash.nl/backend', icon: '🚛' },
    { label: 'PTW4', url: 'https://protruckwash.app/login', icon: '🚿' },
    { label: 'Exact Online', url: 'https://start.exactonline.nl', icon: '📊' },
    { label: 'ING Zakelijk', url: 'https://mijn.ing.nl/zakelijk', icon: '🏦' },
    { label: 'Shopify', url: 'https://admin.shopify.com', icon: '🛒' },
    { label: 'HR Salaris', url: 'https://salaris.nl', icon: '💰' },
  ];

  const INTERNAL_PAGES = [
    { key: 'maandafsluiting', label: 'Maandafsluiting' },
    { key: 'tw1-memo', label: 'TW1 Memo' },
    { key: 'truckshop-memo', label: 'Truckshop Memo' },
    { key: 'ptw-import', label: 'PTW Import' },
    { key: 'incassos', label: 'Incasso\'s' },
    { key: 'betalingen', label: 'Betalingen' },
    { key: 'salaris', label: 'Salaris' },
    { key: 'sepa', label: 'SEPA Batch' },
    { key: 'storno', label: 'Storno\'s' },
    { key: 'logboek', label: 'Logboek' },
    { key: 'instellingen', label: 'Instellingen' },
  ];

  const togglePinShortcut = async (key) => {
    const updated = pinnedShortcuts.includes(key)
      ? pinnedShortcuts.filter(k => k !== key)
      : [...pinnedShortcuts, key];
    setPinnedShortcuts(updated);
    try {
      await api.put('/api/users/shortcuts', { pinned_shortcuts: updated });
    } catch { /* silent */ }
  };

  // Deadline calculations from templates
  const [deadlines, setDeadlines] = useState([]);
  const loadDeadlines = useCallback(async () => {
    try {
      const res = await api.get('/api/dashboard/task-templates');
      const tmpls = res.data.templates || [];
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth();
      const list = [];
      for (const t of tmpls) {
        let deadlineDay = null;
        if (t.day_type === 'fixed' && t.day) {
          deadlineDay = t.day;
        } else if (t.day_type === 'last_day') {
          deadlineDay = new Date(y, m + 1, 0).getDate();
        } else if (t.day_type === 'last_thursday') {
          const last = new Date(y, m + 1, 0);
          while (last.getDay() !== 4) last.setDate(last.getDate() - 1);
          deadlineDay = last.getDate();
        } else if (t.day_type?.startsWith('every_')) {
          const dayName = t.day_type.replace('every_', '');
          const dayIdx = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'].indexOf(dayName);
          if (dayIdx >= 0) {
            let d = new Date(y, m, now.getDate());
            while (d.getDay() !== dayIdx || d.getDate() < now.getDate()) {
              d.setDate(d.getDate() + 1);
              if (d.getMonth() !== m) break;
            }
            if (d.getMonth() === m) deadlineDay = d.getDate();
          }
        }
        if (deadlineDay) {
          const deadlineDate = new Date(y, m, deadlineDay);
          const diffMs = deadlineDate - now;
          const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
          list.push({ label: t.label, icon: t.icon, day: deadlineDay, diffDays, type: t.type, medewerker: t.medewerker });
        }
      }
      list.sort((a, b) => a.day - b.day);
      setDeadlines(list.filter(d => d.diffDays >= -3).slice(0, 7));
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadDeadlines(); }, [loadDeadlines]);

  // Employee task grouping
  const employeeTasks = {};
  tasks.forEach(t => {
    if (t.medewerker) {
      if (!employeeTasks[t.medewerker]) employeeTasks[t.medewerker] = [];
      employeeTasks[t.medewerker].push(t);
    }
  });

  if (loading) return <div className="p-6"><div className="animate-pulse space-y-4"><div className="h-20 bg-gray-100 rounded-xl" /><div className="h-40 bg-gray-100 rounded-xl" /></div></div>;

  const handleSwapDuty = async (date, newEmail) => {
    setSwapping(date);
    try {
      const res = await api.put(`/api/phone-duty/${date}/swap`, { new_user_email: newEmail });
      toast.success(res.data.message);
      // Refresh
      const [todayRes, weekRes] = await Promise.all([api.get('/api/phone-duty/today'), api.get('/api/phone-duty')]);
      setPhoneDutyToday(todayRes.data.duty);
      setPhoneDutyWeek(weekRes.data.days || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ruilen mislukt');
    } finally { setSwapping(null); }
  };

  const NL_DAY_SHORT = ['ma', 'di', 'wo', 'do', 'vr'];
  const todayStr = new Date().toISOString().split('T')[0];

  const WeatherIcon = weather ? getWmoIcon(weather.weathercode) : Cloud;

  // Announcement handlers
  const dismissAnnouncement = async (id) => {
    setAnnouncements(a => a.filter(x => x.id !== id));
    api.post(`/api/announcements/${id}/dismiss`).catch(() => {});
  };

  const deleteAnnouncement = async (id) => {
    setAnnouncements(a => a.filter(x => x.id !== id));
    try {
      await api.delete(`/api/announcements/${id}`);
      toast.success('Aankondiging verwijderd');
    } catch { toast.error('Verwijderen mislukt'); }
  };

  const createAnnouncement = async () => {
    if (!annForm.message.trim()) { toast.error('Vul een bericht in'); return; }
    try {
      const res = await api.post('/api/announcements', {
        message: annForm.message.trim(),
        type: annForm.type,
        expires_at: annForm.expires_at || null,
      });
      setAnnouncements(a => [res.data.announcement, ...a]);
      setShowNewAnnouncement(false);
      setAnnForm({ message: '', type: 'info', expires_at: '' });
      toast.success('Aankondiging geplaatst');
    } catch (err) { toast.error(err.response?.data?.detail || 'Plaatsen mislukt'); }
  };

  const ANN_STYLES = {
    info: { bg: 'from-blue-500/10 to-blue-600/5', border: 'border-blue-400/30', icon: Info, iconColor: 'text-blue-500', textColor: 'text-blue-900' },
    waarschuwing: { bg: 'from-amber-500/10 to-orange-500/5', border: 'border-amber-400/30', icon: AlertTriangle, iconColor: 'text-amber-500', textColor: 'text-amber-900' },
    urgent: { bg: 'from-red-500/10 to-red-600/5', border: 'border-red-400/30', icon: Flame, iconColor: 'text-red-500', textColor: 'text-red-900' },
  };

  // Task detail & comments handlers
  const openTaskDetail = async (task) => {
    setSelectedTask(task);
    setCommentText('');
    setShowMentionDropdown(false);
    try {
      const res = await api.get(`/api/tasks/${task.key}/comments`);
      setTaskComments(res.data.comments || []);
    } catch { setTaskComments([]); }
  };

  const handleCommentChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    setCommentText(val);
    setMentionCursorPos(pos);
    // Check if @ was typed
    const textBefore = val.slice(0, pos);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setMentionFilter(atMatch[1].toLowerCase());
      setShowMentionDropdown(true);
    } else {
      setShowMentionDropdown(false);
    }
  };

  const insertMention = (userName, userEmail) => {
    const textBefore = commentText.slice(0, mentionCursorPos);
    const textAfter = commentText.slice(mentionCursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    const newText = textBefore.slice(0, atIdx) + `@${userName} ` + textAfter;
    setCommentText(newText);
    setShowMentionDropdown(false);
    commentInputRef.current?.focus();
  };

  const submitComment = async () => {
    if (!commentText.trim() || !selectedTask) return;
    // Extract mentions from text
    const mentionMatches = commentText.match(/@(\w+)/g) || [];
    const mentionNames = mentionMatches.map(m => m.slice(1).toLowerCase());
    const mentionEmails = allUsers
      .filter(u => mentionNames.includes((u.naam || '').toLowerCase()))
      .map(u => u.email);
    try {
      const res = await api.post(`/api/tasks/${selectedTask.key}/comments`, {
        text: commentText.trim(),
        mentions: mentionEmails,
      });
      setTaskComments(prev => [...prev, res.data.comment]);
      setCommentText('');
      toast.success('Commentaar geplaatst');
    } catch (err) { toast.error(err.response?.data?.detail || 'Plaatsen mislukt'); }
  };

  const filteredMentionUsers = allUsers.filter(u =>
    (u.naam || '').toLowerCase().includes(mentionFilter)
  );

  // Drag & drop handler for task assignment
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    if (!over || !active) return;
    const taskKey = active.id;
    const targetName = over.id; // employee name
    // Find the task
    const task = tasks.find(t => t.key === taskKey);
    if (!task || task.medewerker === targetName) return;
    // Optimistic update
    const oldMedewerker = task.medewerker;
    setTasks(prev => prev.map(t => t.key === taskKey ? { ...t, medewerker: targetName } : t));
    try {
      await api.put(`/api/tasks/${taskKey}/assign`, { assigned_to: targetName });
      toast.success(`Taak '${task.label}' toegewezen aan ${targetName}`);
    } catch (err) {
      // Revert
      setTasks(prev => prev.map(t => t.key === taskKey ? { ...t, medewerker: oldMedewerker } : t));
      toast.error(err.response?.data?.detail || 'Toewijzing mislukt');
    }
  };

  const activeDragTask = activeDragId ? tasks.find(t => t.key === activeDragId) : null;

  return (
    <div className="tw1-fade-in space-y-5" data-testid="dashboard">

      {/* ═══ AANKONDIGINGEN BANNER ═══ */}
      <AnimatePresence>
        {announcements.map(ann => {
          const style = ANN_STYLES[ann.type] || ANN_STYLES.info;
          const AnnIcon = style.icon;
          return (
            <motion.div
              key={ann.id}
              data-testid={`announcement-${ann.id}`}
              initial={{ opacity: 0, y: -20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -20, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <div className={`relative rounded-xl bg-gradient-to-r ${style.bg} border ${style.border} px-4 py-3 mb-1`}>
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 flex-shrink-0 ${style.iconColor}`}>
                    <AnnIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${style.textColor}`} data-testid={`announcement-message-${ann.id}`}>
                      {ann.message}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {ann.created_by_name || ann.created_by} &middot; {ann.created_at ? new Date(ann.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {admin && (
                      <button
                        onClick={() => deleteAnnouncement(ann.id)}
                        className="p-1 rounded-md hover:bg-black/5 text-gray-400 hover:text-red-500 transition-colors"
                        title="Verwijderen"
                        data-testid={`announcement-delete-${ann.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => dismissAnnouncement(ann.id)}
                      className="p-1 rounded-md hover:bg-black/5 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Sluiten"
                      data-testid={`announcement-dismiss-${ann.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Admin: Nieuwe aankondiging knop */}
      {admin && (
        <div className="flex justify-end -mb-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-[#526071] hover:text-[#003366] h-7"
            onClick={() => setShowNewAnnouncement(true)}
            data-testid="new-announcement-btn"
          >
            <Megaphone className="w-3.5 h-3.5 mr-1" /> Nieuwe aankondiging
          </Button>
        </div>
      )}

      {/* ═══ ROW 1: Welkomstbanner + Weer ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Widget 1: Welkomstbanner */}
        <div className="lg:col-span-3" data-testid="welcome-banner">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#003366] to-[#00244d] p-6">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#f5a800]/10 rounded-full -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
            <div className="relative">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="welcome-greeting">
                    {getGreeting()}, {getFirstName(user)}
                  </h1>
                  <p className="text-sm text-white/60 mt-1 font-medium" data-testid="welcome-date">{getFullDate()}</p>
                  <p className="text-sm text-[#f5a800] mt-2" data-testid="welcome-summary">
                    {isCurrentMonth ? (
                      <>
                        Je hebt vandaag {todayTasks.length} {todayTasks.length === 1 ? 'taak' : 'taken'}
                        {overdueTasks.length > 0 && <span className="text-red-300">, waarvan {overdueTasks.length} overdue</span>}
                        {' '}&middot; {checkedCount}/{tasks.length} afgerond ({taskProgress}%)
                      </>
                    ) : (
                      <>{monthNames[selectedMonth - 1]} {selectedYear}: {checkedCount}/{tasks.length} taken afgerond</>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={prevMonth} className="h-8 w-8 p-0 text-white/60 hover:text-white hover:bg-white/10" data-testid="dashboard-prev-month">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {!isCurrentMonth && (
                    <Button variant="outline" size="sm" onClick={goToCurrentMonth} className="text-xs h-8 border-white/20 text-white hover:bg-white/10" data-testid="dashboard-today-btn">
                      Vandaag
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={nextMonth} className="h-8 w-8 p-0 text-white/60 hover:text-white hover:bg-white/10" data-testid="dashboard-next-month">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Widget 2: Weer */}
        <div className="lg:col-span-1" data-testid="weather-widget">
          <TiltCard glowColor="#f5a800" className="rounded-2xl h-full">
            <div className="relative overflow-hidden rounded-2xl border border-white/60 bg-gradient-to-br from-[#f5a800]/10 to-white backdrop-blur-sm p-5 h-full flex flex-col justify-center items-center gap-2">
              <div className="absolute inset-0 bg-white/40 pointer-events-none" />
              {weather ? (
                <div className="relative text-center">
                  <WeatherIcon className="w-10 h-10 text-[#f5a800] mx-auto mb-1" />
                  <p className="text-3xl font-bold font-mono text-[#0b1220]">{Math.round(weather.temperature)}°C</p>
                  <p className="text-xs text-[#526071] font-medium">{getWmoDesc(weather.weathercode)}</p>
                  <p className="text-[10px] text-[#526071]/70 mt-1">Bodegraven &middot; {Math.round(weather.windspeed)} km/u</p>
                </div>
              ) : (
                <div className="relative text-center">
                  <Cloud className="w-8 h-8 text-gray-300 mx-auto mb-1" />
                  <p className="text-xs text-[#526071]">Weer laden...</p>
                </div>
              )}
            </div>
          </TiltCard>
        </div>
      </div>

      {/* ═══ ROW 2: Telefoondienst + Online ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Widget 9: Telefoondienst vandaag */}
        <div className="tw1-card p-4" data-testid="phone-duty-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0b1220] flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#003366]" /> Telefoondienst
            </h3>
            <span className="text-[10px] text-[#526071] font-mono">10:00 — 14:00</span>
          </div>
          {/* Vandaag */}
          {phoneDutyToday ? (
            <div className="rounded-lg bg-[#003366]/5 border border-[#003366]/10 px-3 py-2.5 mb-3">
              <p className="text-sm font-semibold text-[#003366]" data-testid="phone-duty-today-name">
                Vandaag: {phoneDutyToday.user_name}
              </p>
              {phoneDutyToday.swapped_from && (
                <p className="text-[10px] text-[#526071] mt-0.5 flex items-center gap-1">
                  <ArrowRightLeft className="w-3 h-3" /> Geruild van {phoneDutyToday.swapped_from}
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-lg bg-[#f5a800]/10 border border-[#f5a800]/30 px-3 py-2.5 mb-3" data-testid="phone-duty-today-empty">
              <p className="text-sm font-semibold text-[#f5a800]">Geen telefoondienst ingepland voor vandaag!</p>
            </div>
          )}
          {/* Weekoverzicht */}
          <div className="space-y-1">
            {phoneDutyWeek.map((day, i) => {
              const isToday = day.date === todayStr;
              const isEmpty = !day.user_email;
              const isMyDuty = day.user_email === user?.email;
              return (
                <div key={day.date}
                  className={`flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs transition-colors ${
                    isToday ? 'bg-[#003366]/5 border border-[#003366]/10 font-semibold' : ''
                  } ${isEmpty && !isToday ? 'bg-[#f5a800]/5' : ''}`}
                  data-testid={`phone-duty-day-${i}`}
                >
                  <span className={`w-6 font-mono ${isToday ? 'text-[#003366]' : 'text-[#526071]'}`}>
                    {NL_DAY_SHORT[i]}
                  </span>
                  <span className={`flex-1 ml-2 ${isEmpty ? 'text-[#f5a800] italic' : isToday ? 'text-[#003366]' : 'text-[#0b1220]'}`}>
                    {day.user_name || '— leeg —'}
                  </span>
                  {isMyDuty && (
                    <Select
                      value=""
                      onValueChange={(newEmail) => handleSwapDuty(day.date, newEmail)}
                      disabled={swapping === day.date}
                    >
                      <SelectTrigger className="h-6 w-[72px] text-[10px] border-[#003366]/20">
                        <SelectValue placeholder="Ruilen" />
                      </SelectTrigger>
                      <SelectContent>
                        {phoneDutyEligible.filter(u => u.email !== user?.email).map(u => (
                          <SelectItem key={u.email} value={u.email} className="text-xs">{u.naam}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Widget 3: Wie er online zijn */}
        <div className="tw1-card p-4" data-testid="online-widget">
          <h3 className="text-sm font-semibold text-[#0b1220] mb-3">Online</h3>
          <div className="flex items-center gap-3 flex-wrap">
            {allUsers.map((u, i) => {
              const isOnline = sessions.some(s => s.email === u.email);
              const session = sessions.find(s => s.email === u.email);
              const initial = (u.naam || u.email)?.[0]?.toUpperCase() || '?';
              const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
              return (
                <div key={u.email} className="relative group cursor-default" data-testid={`user-avatar-${u.email}`}>
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm transition-transform hover:scale-110"
                    style={{ backgroundColor: isOnline ? color : '#d1d5db' }}
                  >
                    {initial}
                  </div>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${isOnline ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#0b1220] text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                    <p className="font-medium">{u.naam || u.email}</p>
                    <p className="text-white/60">{u.rol || 'Gebruiker'}</p>
                    <p className="text-white/50">{isOnline ? 'Nu actief' : session?.last_active ? `${Math.round((Date.now() - new Date(session.last_active).getTime()) / 60000)} min geleden` : 'Offline'}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══ ROW 3: Snelkoppelingen (full width) ═══ */}
        <div className="tw1-card p-4" data-testid="shortcuts-widget">
          <h3 className="text-sm font-semibold text-[#0b1220] mb-3">Snelkoppelingen</h3>
          {/* External */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin mb-3">
            {EXTERNAL_LINKS.map(link => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 min-w-[60px] p-2 rounded-xl hover:bg-gray-50 transition-colors group"
                data-testid={`ext-link-${link.label.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#003366]/5 flex items-center justify-center text-lg group-hover:bg-[#003366]/10 transition-colors">
                  {link.icon}
                </div>
                <span className="text-[10px] text-[#526071] text-center leading-tight whitespace-nowrap">{link.label}</span>
              </a>
            ))}
          </div>
          {/* Internal pinned */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin flex-wrap">
            {INTERNAL_PAGES.map(page => {
              const isPinned = pinnedShortcuts.includes(page.key);
              return (
                <button
                  key={page.key}
                  onClick={() => isPinned ? onNavigate(page.key) : togglePinShortcut(page.key)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all ${
                    isPinned ? 'bg-[#003366]/8 text-[#003366] font-medium hover:bg-[#003366]/15' : 'bg-gray-50 text-[#526071] hover:bg-gray-100'
                  }`}
                  data-testid={`pin-${page.key}`}
                >
                  {isPinned ? <Pin className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  {page.label}
                  {isPinned && (
                    <span
                      onClick={(e) => { e.stopPropagation(); togglePinShortcut(page.key); }}
                      className="ml-1 text-[#526071] hover:text-[#c0392b] cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

      {/* Smart Suggestions */}
      {suggestions.length > 0 && (
        <div className="space-y-2" data-testid="smart-suggestions">
          {suggestions.map(s => (
            <div key={s.key} className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
              s.priority === 'high' ? 'bg-[#f5a800]/5 border-[#f5a800]/25' : 'bg-[#003366]/[0.03] border-[#003366]/15'
            }`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                s.priority === 'high' ? 'bg-[#f5a800]/15' : 'bg-[#003366]/10'
              }`}>
                <Bell className={`w-4 h-4 ${s.priority === 'high' ? 'text-[#f5a800]' : 'text-[#003366]'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0b1220]">{s.title}</p>
                <p className="text-xs text-[#526071] truncate">{s.text}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {s.action && (
                  <Button size="sm" className="h-7 text-[11px] bg-[#003366] hover:bg-[#00244d] text-white" onClick={() => onNavigate(s.action)}>
                    <ArrowRight className="w-3 h-3 mr-1" /> Ga naar
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600" onClick={() => dismissSuggestion(s.key)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ ROW 4: Maandtaken + Deadlines ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Widget 4: Maandtaken (verbeterd) */}
        <div className="lg:col-span-2 tw1-card p-5" data-testid="dashboard-timeline">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-[#0b1220] text-sm">Maandtaken — {monthNames[selectedMonth - 1]} {selectedYear}</h3>
              <p className="text-xs text-[#526071] mt-0.5">{checkedCount} van {tasks.length} taken af — {taskProgress}%</p>
            </div>
            <div className="flex items-center gap-2">
              {admin && (
                <Button variant="ghost" size="sm" className="text-xs text-[#526071] h-7" onClick={openTaskMgmt} data-testid="task-mgmt-btn">
                  <Settings className="w-3.5 h-3.5 mr-1" /> Beheer
                </Button>
              )}
              {checkedCount < tasks.length && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-[#526071] h-7"
                  onClick={async () => {
                    const updated = tasks.map(t => ({ ...t, checked: true }));
                    setTasks(updated);
                    try { await api.post(`/api/dashboard/tasks/${selectedYear}/${selectedMonth}/bulk-complete`, { keys: [] }); } catch(e) {}
                  }}
                >
                  Alles afvinken
                </Button>
              )}
            </div>
          </div>
          {/* Progress bar */}
          <div className="mb-4">
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{ width: `${taskProgress}%`, background: taskProgress === 100 ? '#1e8c45' : 'linear-gradient(90deg, #003366, #f5a800)' }}
              />
            </div>
          </div>
          {/* Task list */}
          <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
            {sortedTasks.map((task) => {
              const origIdx = findOrigIndex(task);
              const isPast = isCurrentMonth && task.dag < day;
              const isToday = isCurrentMonth && task.dag === day;
              return (
                <div
                  key={task.key}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                    task.checked ? 'bg-[#1e8c45]/5' :
                    isPast ? 'bg-[#c0392b]/5 border border-[#c0392b]/15' :
                    isToday ? 'bg-[#f5a800]/5 border border-[#f5a800]/20' :
                    'hover:bg-gray-50'
                  }`}
                  data-testid={`task-${task.key}`}
                  onClick={() => openTaskDetail(task)}
                >
                  <Checkbox
                    data-testid={`dashboard-task-toggle-${task.key}`}
                    checked={task.checked}
                    onCheckedChange={() => toggleTask(origIdx)}
                    onClick={(e) => e.stopPropagation()}
                    className="border-gray-300 data-[state=checked]:bg-[#1e8c45] data-[state=checked]:border-[#1e8c45]"
                  />
                  {task.icon && <span className="text-base flex-shrink-0">{task.icon}</span>}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${task.checked ? 'line-through text-[#526071]' : isPast ? 'text-[#c0392b] font-medium' : 'text-[#0b1220]'}`}>
                      {task.label}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {task.type && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          task.type === 'Facturatie' ? 'bg-blue-50 text-blue-600' :
                          task.type === 'Betalingen' ? 'bg-emerald-50 text-emerald-600' :
                          task.type === 'Salaris' ? 'bg-purple-50 text-purple-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>{task.type}</span>
                      )}
                      {task.medewerker && <span className="text-[10px] text-[#526071]">{task.medewerker}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isPast && !task.checked && <span className="text-[10px] font-bold text-[#c0392b] uppercase">te laat</span>}
                    <MessageCircle className="w-3.5 h-3.5 text-gray-300 hover:text-[#003366] transition-colors" />
                    <span className="text-xs font-mono text-[#526071] tabular-nums">{task.dag}e</span>
                    {task.checked ? (
                      <CheckCircle2 className="w-4 h-4 text-[#1e8c45]" />
                    ) : isToday ? (
                      <Clock className="w-4 h-4 text-[#f5a800]" />
                    ) : isPast ? (
                      <AlertCircle className="w-4 h-4 text-[#c0392b]" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-300" />
                    )}
                  </div>
                </div>
              );
            })}
            {tasks.length === 0 && (
              <EmptyState type="tasks" title="Geen taken" subtitle="Er zijn nog geen taken voor deze maand" />
            )}
          </div>
        </div>

        {/* Widget 6: Deadline timeline */}
        <div className="tw1-card p-5" data-testid="deadline-widget">
          <h3 className="font-semibold text-[#0b1220] text-sm mb-3">Komende deadlines</h3>
          <div className="space-y-2">
            {deadlines.length === 0 ? (
              <p className="text-xs text-[#526071]">Geen deadlines gevonden</p>
            ) : (
              deadlines.map((d, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${
                    d.diffDays < 0 ? 'bg-[#c0392b]/5' : d.diffDays <= 1 ? 'bg-[#f5a800]/5' : 'bg-gray-50'
                  }`}
                  data-testid={`deadline-${i}`}
                >
                  <span className="text-base flex-shrink-0">{d.icon || '📋'}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${d.diffDays < 0 ? 'text-[#c0392b] font-medium' : 'text-[#0b1220]'}`}>{d.label}</p>
                    <p className="text-[10px] text-[#526071]">{d.day}e &middot; {d.medewerker || 'Algemeen'}</p>
                  </div>
                  <Badge className={`text-[10px] px-2 ${
                    d.diffDays < 0 ? 'bg-[#c0392b] hover:bg-[#c0392b]' :
                    d.diffDays <= 1 ? 'bg-[#f5a800] hover:bg-[#f5a800] text-white' :
                    d.diffDays <= 3 ? 'bg-[#003366] hover:bg-[#003366]' :
                    'bg-[#1e8c45] hover:bg-[#1e8c45]'
                  } text-white`}>
                    {d.diffDays < 0 ? `${Math.abs(d.diffDays)}d te laat` : d.diffDays === 0 ? 'Vandaag' : d.diffDays === 1 ? 'Morgen' : `nog ${d.diffDays}d`}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ═══ ROW 4: Medewerker overzicht (drag & drop voor admins) ═══ */}
      {Object.keys(employeeTasks).length > 0 && (
        <DndContext sensors={sensors} onDragStart={(e) => setActiveDragId(e.active.id)} onDragEnd={handleDragEnd} onDragCancel={() => setActiveDragId(null)}>
          <div className="tw1-card p-5" data-testid="employee-widget">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0b1220] text-sm">Medewerker taken — {monthNames[selectedMonth - 1]}</h3>
              {admin && <span className="text-[10px] text-[#526071] flex items-center gap-1"><GripVertical className="w-3 h-3" /> Sleep taken om toe te wijzen</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(employeeTasks).map(([name, empTasks], idx) => {
                const done = empTasks.filter(t => t.checked).length;
                const total = empTasks.length;
                const pct = Math.round((done / total) * 100);
                const allDone = done === total;
                return (
                  <DroppableEmployee key={name} id={name}>
                    <div className={`rounded-xl border p-4 transition-all ${allDone ? 'border-[#1e8c45]/30 bg-[#1e8c45]/5' : 'border-gray-200'}`} data-testid={`employee-card-${name}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs"
                          style={{ backgroundColor: AVATAR_COLORS[idx % AVATAR_COLORS.length] }}
                        >
                          {name[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0b1220] truncate">{name}</p>
                          <p className="text-[10px] text-[#526071]">{done}/{total} taken ({pct}%)</p>
                        </div>
                        {allDone && <CheckCircle2 className="w-5 h-5 text-[#1e8c45] flex-shrink-0" />}
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, backgroundColor: allDone ? '#1e8c45' : '#003366' }}
                        />
                      </div>
                      <div className="space-y-1 max-h-[160px] overflow-y-auto">
                        {empTasks.map(t => {
                          const origIdx = findOrigIndex(t);
                          const isPast = isCurrentMonth && t.dag < day && !t.checked;
                          const taskRow = (
                            <div className={`flex items-center gap-2 text-xs ${admin ? 'cursor-grab active:cursor-grabbing' : ''}`}>
                              {admin && <GripVertical className="w-3 h-3 text-gray-300 flex-shrink-0" />}
                              {admin ? (
                                <Checkbox
                                  checked={t.checked}
                                  onCheckedChange={() => toggleTask(origIdx)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-3.5 w-3.5 border-gray-300 data-[state=checked]:bg-[#1e8c45] data-[state=checked]:border-[#1e8c45]"
                                  data-testid={`emp-task-toggle-${t.key}`}
                                />
                              ) : (
                                t.checked ? <CheckCircle2 className="w-3.5 h-3.5 text-[#1e8c45] flex-shrink-0" /> : <Circle className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                              )}
                              <span className={`truncate ${t.checked ? 'line-through text-[#526071]' : isPast ? 'text-[#c0392b]' : 'text-[#0b1220]'}`}>
                                {t.label}
                              </span>
                              <span className="ml-auto text-[10px] text-[#526071] font-mono flex-shrink-0">{t.dag}e</span>
                            </div>
                          );
                          return admin ? (
                            <DraggableTask key={t.key} id={t.key}>
                              {taskRow}
                            </DraggableTask>
                          ) : (
                            <div key={t.key}>{taskRow}</div>
                          );
                        })}
                      </div>
                    </div>
                  </DroppableEmployee>
                );
              })}
            </div>
          </div>
          {/* Drag overlay */}
          <DragOverlay>
            {activeDragTask ? (
              <div className="bg-white border border-[#f5a800] rounded-lg px-3 py-2 shadow-lg text-xs font-medium text-[#0b1220]">
                {activeDragTask.icon} {activeDragTask.label}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* ═══ ROW 5: Recente activiteit ═══ */}
      <div className="tw1-card p-5" data-testid="activity-widget">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#0b1220] text-sm">Recente activiteit</h3>
          <button
            onClick={() => onNavigate('instellingen')}
            className="text-xs text-[#003366] hover:underline"
            data-testid="activity-see-all"
          >
            Bekijk alle activiteiten &rarr;
          </button>
        </div>
        {activities.length === 0 ? (
          <p className="text-xs text-[#526071]">Nog geen activiteiten vandaag</p>
        ) : (
          <div className="space-y-2">
            {activities.map((act, i) => {
              const initial = (act.gebruiker || '?')[0].toUpperCase();
              const timeAgo = act.datum ? (() => {
                const diff = Math.round((Date.now() - new Date(act.datum).getTime()) / 60000);
                if (diff < 1) return 'Zojuist';
                if (diff < 60) return `${diff} min geleden`;
                if (diff < 1440) return `${Math.round(diff / 60)} uur geleden`;
                return `${Math.round(diff / 1440)} dagen geleden`;
              })() : '';
              return (
                <div key={act.id || i} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0" data-testid={`activity-${i}`}>
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                    style={{ backgroundColor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                  >
                    {initial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#0b1220]">
                      <span className="font-medium">{act.gebruiker}</span> {act.actie?.toLowerCase()}
                    </p>
                    {act.details && <p className="text-xs text-[#526071] truncate">{act.details}</p>}
                  </div>
                  <span className="text-[10px] text-[#526071] whitespace-nowrap flex-shrink-0">{timeAgo}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Task Management Dialog */}
      <Dialog open={showTaskMgmt} onOpenChange={setShowTaskMgmt}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Maandtaken beheren</DialogTitle>
          </DialogHeader>

          {editingTmpl ? (
            <div className="space-y-4 p-1">
              <div className="space-y-2">
                <label className="text-xs font-medium text-[#526071]">Naam</label>
                <Input value={tmplForm.label} onChange={e => setTmplForm(f => ({ ...f, label: e.target.value }))} placeholder="Taaknaam" className="h-9" data-testid="tmpl-label-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-[#526071]">Icoon</label>
                  <div className="flex flex-wrap gap-1">
                    {EMOJI_OPTIONS.map(e => (
                      <button key={e} onClick={() => setTmplForm(f => ({ ...f, icon: e }))} className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${tmplForm.icon === e ? 'bg-[#003366]/10 ring-2 ring-[#003366]' : 'hover:bg-gray-100'}`}>{e}</button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-[#526071]">Type</label>
                  {showAddType ? (
                    <div className="flex gap-1">
                      <Input value={newTypeName} onChange={e => setNewTypeName(e.target.value)} placeholder="Nieuw type..." className="h-9 text-sm" autoFocus />
                      <Button size="sm" className="h-9 px-2" onClick={() => { if (newTypeName.trim()) { setTmplForm(f => ({ ...f, type: newTypeName.trim() })); setShowAddType(false); setNewTypeName(''); } }}>
                        <Save className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-9 px-2" onClick={() => setShowAddType(false)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <Select value={tmplForm.type} onValueChange={v => setTmplForm(f => ({ ...f, type: v }))}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{allTaskTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" className="h-9 w-9 p-0 flex-shrink-0" onClick={() => setShowAddType(true)} title="Nieuw type toevoegen">
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-[#526071]">Planning</label>
                  {showAddDayType ? (
                    <div className="space-y-1">
                      <Input value={newDayTypeName} onChange={e => { setNewDayTypeName(e.target.value); setNewDayTypeKey(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_')); }} placeholder="Naam (bijv. Elke maandag)" className="h-9 text-sm" autoFocus />
                      <div className="flex gap-1">
                        <Button size="sm" className="h-8 text-xs" onClick={() => { if (newDayTypeName.trim() && newDayTypeKey.trim()) { setTmplForm(f => ({ ...f, day_type: newDayTypeKey.trim() })); setShowAddDayType(false); setNewDayTypeName(''); setNewDayTypeKey(''); } }}>
                          <Save className="w-3 h-3 mr-1" /> Toevoegen
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setShowAddDayType(false)}>Annuleren</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <Select value={tmplForm.day_type} onValueChange={v => setTmplForm(f => ({ ...f, day_type: v }))}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{allDayTypes.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" className="h-9 w-9 p-0 flex-shrink-0" onClick={() => setShowAddDayType(true)} title="Nieuw planningtype toevoegen">
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              {tmplForm.day_type === 'fixed' && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-[#526071]">Dag van de maand</label>
                  <Input type="number" min={1} max={31} value={tmplForm.day} onChange={e => setTmplForm(f => ({ ...f, day: parseInt(e.target.value) || 1 }))} className="h-9 w-24 font-mono" />
                </div>
              )}
              <div className="space-y-2">
                <label className="text-xs font-medium text-[#526071]">Medewerker (optioneel)</label>
                <Select value={tmplForm.medewerker || '_none'} onValueChange={v => setTmplForm(f => ({ ...f, medewerker: v === '_none' ? '' : v }))}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Niemand toegewezen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Niemand toegewezen</SelectItem>
                    <SelectItem value="Johannes">Johannes</SelectItem>
                    <SelectItem value="Jesse">Jesse</SelectItem>
                    <SelectItem value="Kick">Kick</SelectItem>
                    <SelectItem value="Milos">Milos</SelectItem>
                    <SelectItem value="Iris">Iris</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={saveTmpl} className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white text-xs" size="sm" data-testid="tmpl-save-btn">
                  <Save className="w-3.5 h-3.5 mr-1" /> Opslaan
                </Button>
                <Button variant="outline" onClick={() => setEditingTmpl(null)} className="text-xs" size="sm">
                  <X className="w-3.5 h-3.5 mr-1" /> Annuleren
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 p-1">
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#526071]">{templates.length} taaksjablonen</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="text-xs" onClick={startAddTmpl} data-testid="tmpl-add-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Toevoegen
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={regenerateTasks} data-testid="tmpl-regen-btn">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenereer taken
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                {templates.map(t => (
                  <div key={t.key} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 group">
                    <span className="text-lg">{t.icon || '📄'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0b1220] truncate">{t.label}</p>
                      <p className="text-xs text-[#526071]">
                        {allDayTypes.find(d => d.value === t.day_type)?.label || t.day_type}
                        {t.day_type === 'fixed' && t.day ? ` (${t.day}e)` : ''}
                        {' · '}{t.type}
                        {t.medewerker ? ` · ${t.medewerker}` : ''}
                      </p>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEditTmpl(t)}>
                        <Settings className="w-3.5 h-3.5 text-[#526071]" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => deleteTmpl(t.key)}>
                        <Trash2 className="w-3.5 h-3.5 text-[#c0392b]" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-[#526071] pt-2 border-t border-gray-100">
                Na wijzigingen: klik "Regenereer taken" om de huidige maand bij te werken. Bestaande afvinkingen blijven behouden.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Announcement Modal */}
      <Dialog open={showNewAnnouncement} onOpenChange={setShowNewAnnouncement}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#003366]" /> Nieuwe aankondiging
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-xs font-medium text-[#526071]">Bericht</label>
              <Textarea
                value={annForm.message}
                onChange={e => setAnnForm(f => ({ ...f, message: e.target.value }))}
                placeholder="Typ je aankondiging..."
                className="min-h-[80px] text-sm"
                data-testid="announcement-message-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-xs font-medium text-[#526071]">Type</label>
                <Select value={annForm.type} onValueChange={v => setAnnForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="h-9" data-testid="announcement-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">
                      <span className="flex items-center gap-2"><Info className="w-3.5 h-3.5 text-blue-500" /> Info</span>
                    </SelectItem>
                    <SelectItem value="waarschuwing">
                      <span className="flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Waarschuwing</span>
                    </SelectItem>
                    <SelectItem value="urgent">
                      <span className="flex items-center gap-2"><Flame className="w-3.5 h-3.5 text-red-500" /> Urgent</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-[#526071]">Vervalt op (optioneel)</label>
                <Input
                  type="date"
                  value={annForm.expires_at}
                  onChange={e => setAnnForm(f => ({ ...f, expires_at: e.target.value }))}
                  className="h-9 text-sm"
                  data-testid="announcement-expires-input"
                />
              </div>
            </div>
            {/* Preview */}
            {annForm.message.trim() && (
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-[#526071] uppercase tracking-wider">Voorbeeld</label>
                <div className={`rounded-xl bg-gradient-to-r ${(ANN_STYLES[annForm.type] || ANN_STYLES.info).bg} border ${(ANN_STYLES[annForm.type] || ANN_STYLES.info).border} px-4 py-3`}>
                  <div className="flex items-start gap-3">
                    {(() => { const PreviewIcon = (ANN_STYLES[annForm.type] || ANN_STYLES.info).icon; return <PreviewIcon className={`w-5 h-5 mt-0.5 ${(ANN_STYLES[annForm.type] || ANN_STYLES.info).iconColor}`} />; })()}
                    <p className={`text-sm font-medium ${(ANN_STYLES[annForm.type] || ANN_STYLES.info).textColor}`}>{annForm.message}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowNewAnnouncement(false)} className="text-xs">
                Annuleren
              </Button>
              <Button
                size="sm"
                className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white text-xs"
                onClick={createAnnouncement}
                data-testid="announcement-submit-btn"
              >
                <Megaphone className="w-3.5 h-3.5 mr-1" /> Plaatsen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Detail & Comments Modal */}
      <Dialog open={!!selectedTask} onOpenChange={(open) => { if (!open) setSelectedTask(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              {selectedTask?.icon && <span className="text-lg">{selectedTask.icon}</span>}
              {selectedTask?.label}
            </DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4 pt-1">
              {/* Task info */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="text-[10px] bg-[#003366] text-white">{selectedTask.dag}e van de maand</Badge>
                {selectedTask.type && <Badge variant="outline" className="text-[10px]">{selectedTask.type}</Badge>}
                {selectedTask.medewerker && <Badge variant="outline" className="text-[10px]">{selectedTask.medewerker}</Badge>}
                <Badge className={`text-[10px] ${selectedTask.checked ? 'bg-[#1e8c45] text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {selectedTask.checked ? 'Afgerond' : 'Open'}
                </Badge>
              </div>

              {/* Comments section */}
              <div className="border-t border-gray-100 pt-3">
                <h4 className="text-xs font-semibold text-[#526071] mb-3 flex items-center gap-1.5">
                  <MessageCircle className="w-3.5 h-3.5" /> Commentaren ({taskComments.length})
                </h4>
                <div className="space-y-2 max-h-[240px] overflow-y-auto mb-3">
                  {taskComments.length === 0 ? (
                    <p className="text-xs text-[#526071] italic">Nog geen commentaren. Wees de eerste!</p>
                  ) : (
                    taskComments.map(c => (
                      <div key={c.id} className="flex gap-2.5" data-testid={`comment-${c.id}`}>
                        <div className="w-7 h-7 rounded-full bg-[#003366]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[10px] font-bold text-[#003366]">{(c.user_name || '?')[0]}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-[#0b1220]">{c.user_name}</span>
                            <span className="text-[10px] text-[#526071]">
                              {c.created_at ? new Date(c.created_at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                          <p className="text-sm text-[#0b1220] mt-0.5">
                            {c.text.split(/(@\w+)/g).map((part, i) =>
                              part.startsWith('@') ? (
                                <span key={i} className="bg-[#003366]/10 text-[#003366] font-medium px-1 rounded text-xs">{part}</span>
                              ) : (
                                <span key={i}>{part}</span>
                              )
                            )}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Comment input with @-autocomplete */}
                <div className="relative">
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        ref={commentInputRef}
                        value={commentText}
                        onChange={handleCommentChange}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !showMentionDropdown) {
                            e.preventDefault();
                            submitComment();
                          }
                        }}
                        placeholder="Schrijf een commentaar... typ @ voor mentions"
                        className="h-9 text-sm pr-8"
                        data-testid="comment-input"
                      />
                      <button
                        onClick={() => { setShowMentionDropdown(!showMentionDropdown); setMentionFilter(''); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#003366]"
                        title="Mention"
                      >
                        <AtSign className="w-4 h-4" />
                      </button>
                      {/* Mention dropdown */}
                      {showMentionDropdown && (
                        <div className="absolute bottom-full left-0 w-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[160px] overflow-y-auto" data-testid="mention-dropdown">
                          {filteredMentionUsers.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-[#526071]">Geen gebruikers gevonden</p>
                          ) : (
                            filteredMentionUsers.map(u => (
                              <button
                                key={u.email}
                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#003366]/5 transition-colors text-left"
                                onClick={() => insertMention(u.naam || u.email.split('@')[0], u.email)}
                                data-testid={`mention-user-${u.email}`}
                              >
                                <div className="w-6 h-6 rounded-full bg-[#003366]/10 flex items-center justify-center">
                                  <span className="text-[10px] font-bold text-[#003366]">{(u.naam || u.email)[0]}</span>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-[#0b1220]">{u.naam || u.email.split('@')[0]}</p>
                                  <p className="text-[10px] text-[#526071]">{u.email}</p>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="h-9 px-3 bg-[#003366] hover:bg-[#00244d] text-white"
                      onClick={submitComment}
                      disabled={!commentText.trim()}
                      data-testid="comment-submit-btn"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
